import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Dialog from "../dialog/dialog";
import { useSelector } from "react-redux";
import { getUid } from "../../redux/authSlice";
import { Box, CircularProgress, Grid, IconButton, Radio, Typography } from "@mui/material";
import Button from "../button/button";
import ControlledInput from "../input/controlled-input";
import InputSearchIcon from "../../assets/icon/input-search";
import useDebounce from "../../hooks/useDebounce";
import { post } from "../../api/setting/actions";
import * as yup from "yup";
import { EMAIL_REGEX } from "../../utils/utils.service";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import MuiPhone from "../input/mui-phone-input";
import DealerAvatarIcon from "../../assets/icon/dealer-avatar";
import CarPlaceholderIcon from "../../assets/icon/car-placeholder";
import { ref, update } from "firebase/database";
import { firebaseDb } from "../../firebase/app";
import { KeyboardBackspace } from "@mui/icons-material";
import AddCarIcon from "../../assets/icon/add-car";
import { useResponsive } from "../../hooks/use-media-query";
import Snackbar from "../../components/snackbar/snackbar";
import { getActiveConId } from "../../redux/userSlice";
import { getData } from "../../utils/firebase";
import { useInfiniteQuery, useQueries, useQuery } from "@tanstack/react-query";
import { CarResponse } from "../../pages/auto-marketing/auto-marketing";
import { CarType } from "../../pages/auto-marketing/auto-marketing";
import CarItem from "./car-item";
import DealForm, { DealFormValues, emptyDealValues } from "./deal-form";
import { createDeal, dateToISODate } from "../../api/deal/dealApi";
import useReviewMaxEnabled from "../../hooks/useReviewMaxEnabled";
import useUsersQuery from "../../hooks/useUsersQuery";

type CarListMode = "lead-car" | "search";

type SoldReviewState = "review" | "select-customer" | "add-customer" | "select-car" | "deal";

interface SoldReviewProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  cars: Car[];
  defaultCars?: Car[];
  defaultState?: "review" | "select-customer" | "add-customer" | "select-car";
  defaultVIN?: string;
  handleClose?: () => void;
  isPage?: boolean;
  carListMode?: CarListMode;
  defaultCustomerID?: string;
  notificationId?: string;
  appointmentId?: string;
  /** Called once the sale has been logged successfully, before the dialog closes. */
  onSoldReviewSuccess?: () => void;
}

const schema = yup.object().shape({
  firstName: yup.string(),
  lastName: yup.string(),
  phoneNumber: yup.string(),
  email: yup
    .string()
    .email("Please enter a valid email")
    .matches(EMAIL_REGEX, "Please enter a valid email"),
});

interface FormInputs {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email?: string;
}

export interface Car {
  vin: string;
  year: number;
  make: string;
  model: string;
  priceOnWebsite: number;
  stockNumber: number;
  photoURL: string;
}

interface Customer {
  convId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  customerId: string;
}

const SoldReview: React.FC<SoldReviewProps> = ({
  open,
  setOpen,
  cars,
  defaultState,
  defaultVIN,
  handleClose,
  isPage,
  carListMode,
  defaultCustomerID,
  notificationId,
  appointmentId,
  onSoldReviewSuccess,
}) => {
  const pageSize = 10;

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const reviewMaxEnabled = useReviewMaxEnabled();

  const [state, setState] = useState<SoldReviewState>(defaultState ?? "review");
  /** The step the "Deal" page was reached from — drives Back and the submit payload. */
  const [previousState, setPreviousState] = useState<SoldReviewState>(
    defaultState ?? "select-customer"
  );

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchLoading, setSearchLoading] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<Customer[]>([]);
  const [selectedVin, setSelectedVin] = useState<string | null>(defaultVIN ?? null);
  const [isSendReview, setIsSendReview] = useState<boolean>(reviewMaxEnabled);
  const [dealValues, setDealValues] = useState<DealFormValues>(emptyDealValues);
  // Until the user edits them, sale price / salesperson stay synced to the
  // selected vehicle and the conversation's assignee.
  const [priceTouched, setPriceTouched] = useState<boolean>(false);
  const [salesTouched, setSalesTouched] = useState<boolean>(false);
  const [soldLoading, setSoldLoading] = useState<boolean>(false);
  const [notify, setNotify] = useState({ message: "", open: false, type: "" });
  const [carsListMode, setCarsListMode] = useState<CarListMode>(carListMode ?? "lead-car");
  const [carSearchTerm, setCarSearchTerm] = useState<string>("");

  const [selectedCustomerID, setSelectedCustomerID] = useState<string | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const debouncedCarSearchTerm = useDebounce(carSearchTerm, 300);

  const uid = useSelector(getUid);
  const activeConId = useSelector(getActiveConId);
  const { isSmall } = useResponsive();
  const { data: users } = useUsersQuery();

  /**
   * The step the scenario is really on — "deal" is a tail step appended to
   * whichever picker the user came from, so data loading and the submit payload
   * keep keying off the originating step.
   */
  const sourceState = state === "deal" ? previousState : state;

  const getCars = useCallback(
    async ({ pageParam }: { pageParam: number }): Promise<CarResponse> => {
      const filtersData = {
        operation: "search_cars_front",
        sort: "createdDate",
        order: "desc",
        size_: pageSize,
        from_: pageParam * pageSize,
        ...(debouncedCarSearchTerm && { query_text: debouncedCarSearchTerm }),
      };
      const r = await post(filtersData, "salesagent-elastic-search");
      const list = r.docs.map((item: any, id: number) => ({ ...item, id: id + 1 })) as CarType[];
      return { docs: list, total: r.total };
    },
    [debouncedCarSearchTerm]
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteQuery<CarResponse>({
      queryKey: ["cars-sold-review", debouncedCarSearchTerm, pageSize],
      queryFn: ({ pageParam }) => getCars({ pageParam: pageParam as number }),
      getNextPageParam: (lastPage, allPages) => {
        const totalLoaded = allPages.reduce((sum, page) => sum + page.docs.length, 0);
        return totalLoaded < lastPage.total ? allPages.length : undefined;
      },
      initialPageParam: 0,
    });

  const carsSearchResults = useMemo(() => data?.pages.flatMap((page) => page.docs) ?? [], [data]);

  const getConvCarsData = useCallback(async () => {
    const path = `conversations/${uid}/${activeConId}/car`;
    try {
      const res = await getData(path);
      return Object.entries(res || {})
        .sort((a: any, b: any) => b[1].timestamp - a[1].timestamp)
        .map(([key, item]: any) => ({ ...item, vin: key }));
    } catch (error) {
      console.log(error);
      return null;
    }
  }, [activeConId, uid]);

  const { data: convCarsData } = useQuery({
    queryKey: ["convCarsData", uid, activeConId],
    queryFn: () => getConvCarsData(),
    enabled: !!uid && !!activeConId && sourceState === "select-car",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleChangeCarsListMode = () => {
    setCarsListMode("search");
  };

  const {
    control,
    getValues,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<FormInputs>({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const onClose = useCallback(() => {
    setOpen(false);
    setState("review");
    setPreviousState("select-customer");
    setSelectedVin(null);
    setSelectedCustomerID(null);
    setSearchTerm("");
    setSearchResults([]);
    reset();
    setIsSendReview(reviewMaxEnabled);
    setDealValues(emptyDealValues());
    setPriceTouched(false);
    setSalesTouched(false);
    setSearchLoading(false);
  }, [reset, reviewMaxEnabled, setOpen]);

  /** Leave the picker and open the Deal page. */
  const goToDeal = useCallback(() => {
    setPreviousState(state);
    setState("deal");
  }, [state]);

  const handleChangeDeal = useCallback((patch: Partial<DealFormValues>) => {
    if ("salePrice" in patch) setPriceTouched(true);
    if ("salesPerson" in patch) setSalesTouched(true);
    setDealValues((current) => ({ ...current, ...patch }));
  }, []);

  const searchLeads = useCallback(() => {
    post(
      {
        uid,
        query_text: debouncedSearchTerm || defaultVIN,
        from_: 0,
        size_: isPage ? 10 : 3,
      },
      "salesagent-elastic-search"
    )
      .then((data) => {
        setSearchResults(data.docs);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setSearchLoading(false);
      });
  }, [uid, debouncedSearchTerm, defaultVIN, isPage]);

  const sendAvailable = (vin: string) => {
    const path = `cars/${uid}/${vin}`;
    const data = {
      isCarSold: false,
      soldChangedByDealer: true,
      soldReview: true,
    };
    update(ref(firebaseDb, path), data)
      .then(() => {
        console.log("data updated");
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getCarWithVIN = useCallback(
    async (vin: string) => {
      const path = `cars/${uid}/${vin}`;
      try {
        const res = await getData(path);
        return res;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    [uid]
  );

  const carSoldQueries = useQueries({
    queries: (convCarsData ?? []).map((car: any) => ({
      queryKey: ["carSold", uid, car.vin],
      queryFn: () => getCarWithVIN(car.vin),
      enabled:
        sourceState === "select-car" &&
        Array.isArray(convCarsData) &&
        convCarsData.length > 0 &&
        !!car.vin,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  const carsAlldata = useMemo(() => {
    if (!convCarsData || convCarsData.length === 0 || carSoldQueries.length === 0) {
      return [];
    }
    return convCarsData.map((car: any, index: number) => {
      const soldData = carSoldQueries[index]?.data || null;
      return { ...car, ...(soldData || {}) };
    });
  }, [convCarsData, carSoldQueries]);

  /* ------------------------------- Deal page ------------------------------- */

  /** The customer record picked from the search list, when there is one. */
  const selectedRecord = useMemo(
    () =>
      selectedCustomerID
        ? searchResults.find((customer) => customer.convId === selectedCustomerID) ?? null
        : null,
    [searchResults, selectedCustomerID]
  );

  /**
   * Conversation the sale belongs to. The `select-car` scenario always sells to
   * the open conversation; the customer pickers resolve it from the selection.
   */
  const dealConvId = useMemo(() => {
    if (sourceState === "select-car") return defaultCustomerID ?? activeConId ?? null;
    return selectedRecord?.convId ?? defaultCustomerID ?? null;
  }, [activeConId, defaultCustomerID, selectedRecord, sourceState]);

  // The conversation doc backs the deal's contact details and the default
  // salesperson (its assignee). Only read once the Deal page is reached.
  const { data: dealConv } = useQuery({
    queryKey: ["sold-review-conv", uid, dealConvId],
    queryFn: async () => {
      try {
        return (await getData(`conversations/${uid}/${dealConvId}`)) as any;
      } catch (error) {
        console.log(error);
        return null;
      }
    },
    enabled: !!uid && !!dealConvId && state === "deal",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // The inventory record for the picked VIN — fills the recap strip and the
  // default sale price when the vehicle didn't come from a loaded list.
  const { data: selectedCarDoc } = useQuery({
    queryKey: ["carSold", uid, selectedVin],
    queryFn: () => getCarWithVIN(selectedVin as string),
    enabled: !!uid && !!selectedVin && state === "deal",
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const selectedCar = useMemo(() => {
    if (!selectedVin) return null;
    const pool: any[] = [...(cars ?? []), ...carsAlldata, ...carsSearchResults];
    const listed = pool.find((c: any) => (c?.vinNumber || c?.vin) === selectedVin) ?? null;
    if (!listed && !selectedCarDoc) return null;
    return { ...((selectedCarDoc as any) ?? {}), ...(listed ?? {}) };
  }, [cars, carsAlldata, carsSearchResults, selectedCarDoc, selectedVin]);

  /** Contact details for the deal write, resolved per scenario. */
  const dealContact = useCallback(() => {
    if (selectedRecord) {
      return {
        name: [selectedRecord.firstName, selectedRecord.lastName].filter(Boolean).join(" ").trim(),
        phone: selectedRecord.phone ?? "",
        email: selectedRecord.email ?? "",
      };
    }
    if (sourceState === "add-customer") {
      return {
        name: [getValues("firstName"), getValues("lastName")].filter(Boolean).join(" ").trim(),
        phone: getValues("phoneNumber") ?? "",
        email: getValues("email") ?? "",
      };
    }
    return {
      name: [dealConv?.firstName, dealConv?.lastName].filter(Boolean).join(" ").trim(),
      phone: dealConv?.phoneNumber ?? "",
      email: dealConv?.email ?? "",
    };
  }, [dealConv, getValues, selectedRecord, sourceState]);

  // Default the sale price to the selected vehicle's price, until the user edits it.
  useEffect(() => {
    if (priceTouched || state !== "deal") return;
    const price = selectedCar?.priceOnWebsite ?? selectedCar?.price;
    const next = price !== undefined && price !== null && price !== "" ? String(price) : "";
    setDealValues((current) =>
      current.salePrice === next ? current : { ...current, salePrice: next }
    );
  }, [priceTouched, selectedCar, state]);

  // Default the salesperson to the conversation's assignee, until the user edits
  // it. Ignored when the assignee isn't a selectable staff member ("all", a
  // deactivated user), so the dropdown never holds an out-of-range value.
  useEffect(() => {
    if (salesTouched || state !== "deal") return;
    const assigneeUid = dealConv?.assignee ? String(dealConv.assignee) : "";
    const assignee = (users ?? []).some((user) => user.uid === assigneeUid) ? assigneeUid : "";
    setDealValues((current) =>
      current.salesPerson === assignee ? current : { ...current, salesPerson: assignee }
    );
  }, [dealConv, salesTouched, state, users]);

  /**
   * Mark as sold. Two writes fan out from here:
   *
   *   - `salesagent-dashboard-backend/deal` — the deal itself. Never gated:
   *     every dealership logs its sales.
   *   - `salesagent-MaxReview-v2/sold-review` — starts (or explicitly skips) the
   *     review sequence. Review Max only; dealerships without the flag never see
   *     the "Send Review Request" step, so the call is skipped entirely.
   */
  const sendSoldReview = useCallback(() => {
    setSoldLoading(true);

    // Prefer sending customerId directly (more reliable than conversationID lookup).
    // Fall back to conversationID when only that is available (e.g. active conv flow).
    const resolvedCustomerId = selectedRecord?.customerId ?? null;
    const resolvedConvId = selectedRecord?.convId ?? null;

    const saleDate = new Date(dealValues.saleDate);
    const contact = dealContact();

    const calls: Promise<unknown>[] = [
      createDeal({
        VINNumber: selectedVin ?? "",
        convID: dealConvId ?? "",
        name: contact.name,
        phone: contact.phone,
        email: contact.email,
        salePrice: dealValues.salePrice.trim(),
        gross: dealValues.gross.trim(),
        saleDate: dateToISODate(saleDate),
        saleDateTs: String(saleDate.getTime()),
        dealType: dealValues.dealType,
        tradeIn: dealValues.tradeIn.trim(),
        salesPerson: dealValues.salesPerson,
      }),
    ];

    if (reviewMaxEnabled) {
      const data = {
        uid,
        // Existing customer selected from search — send customerId
        ...(resolvedCustomerId && { customerId: resolvedCustomerId }),
        // No search-selected customer but we have an active/default conversation
        ...(!resolvedCustomerId && defaultCustomerID && { conversationID: defaultCustomerID }),
        // select-car scenario: always use the active conversation id
        ...(sourceState === "select-car" && {
          conversationID: defaultCustomerID ?? activeConId,
        }),
        // New customer typed in manually
        ...(!resolvedCustomerId &&
          !defaultCustomerID &&
          sourceState !== "select-car" && {
            customerData: {
              firstName: getValues("firstName"),
              lastName: getValues("lastName"),
              email: getValues("email"),
              phoneNumber:
                String(getValues("phoneNumber")).length > 2 ? getValues("phoneNumber") : null,
            },
          }),
        // Carry convId as optional hint even when customerId is known
        ...(resolvedConvId && !resolvedCustomerId && { conversationID: resolvedConvId }),
        vin: selectedVin,
        ...(notificationId && { notificationId }),
        ...(appointmentId && { appointmentId }),
        reviewType: "soldReview",
        reviewNotes: "Customer requested follow-up call for service appointment",
        // Legacy semantics: `reviewStatus` is true when the review is being
        // *skipped*, hence the inversion of the "Send Review Request" checkbox.
        reviewStatus: !isSendReview,
      };
      // post(data, "salesagent-MaxReview")
      calls.push(post(data, "salesagent-MaxReview-v2/sold-review"));
    }

    Promise.all(calls)
      .then(() => {
        console.log("sold review sent successfully");
        onSoldReviewSuccess?.();
        // Always close the dialog and reset state on success
        if (handleClose) {
          handleClose();
        }
        onClose();
      })
      .catch((err) => {
        setNotify({
          message: "Something went wrong, Please try again later.",
          open: true,
          type: "error",
        });
        console.log(err);
      })
      .finally(() => {
        setSoldLoading(false);
      });
  }, [
    defaultCustomerID,
    selectedRecord,
    dealContact,
    dealConvId,
    dealValues,
    reviewMaxEnabled,
    uid,
    sourceState,
    activeConId,
    getValues,
    selectedVin,
    notificationId,
    appointmentId,
    isSendReview,
    handleClose,
    onClose,
    onSoldReviewSuccess,
  ]);

  useEffect(() => {
    if (state === "select-car" && convCarsData && convCarsData.length > 0) {
      setSelectedVin(convCarsData[0].vin);
    }
  }, [convCarsData, state]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;

    if (!scrollContainer || !hasNextPage) return;

    const handleScroll = () => {
      if (isFetchingNextPage) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
      // Trigger when user scrolls within 100px of the bottom
      if (scrollHeight - scrollTop <= clientHeight + 50) {
        fetchNextPage();
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const renderSelectCustomer = useMemo(() => {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Box sx={{ display: "flex", flexDirection: "row", gap: "12px", alignItems: "center" }}>
          <ControlledInput
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchLoading(true);
              setSearchTerm(e.target.value);
            }}
            placeholder="Search customer by name, email or phone"
            sx={{ height: "40px" }}
            startAdornment={
              <InputSearchIcon sx={{ fill: "#C6C6C6", width: "16px", marginRight: "12px" }} />
            }
          />
          <Button
            variant="outlined"
            onClick={() => setState("add-customer")}
            startIcon={<AddCarIcon />}
            sx={{
              minWidth: "156px",
              // borderColor: "#E2E8F0",
              height: "40px",
              // "&:hover": { backgroundColor: "#E2E8F0" },
            }}
          >
            Add Customer
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            maxHeight: isPage ? `calc(100dvh - ${isSmall ? "500px" : "300px"})` : "300px",
            height: isPage ? "fit-content" : "300px",
            overflowY: "auto",
            paddingRight: "4px",
          }}
        >
          {searchLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                paddingY: "24px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : searchResults.length === 0 ? (
            searchTerm.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  width: "100%",
                }}
              >
                <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#b9b9b9" }}>
                  No customers found
                </Typography>
              </Box>
            ) : (
              ""
            )
          ) : (
            searchResults.map((searchItem) => (
              <Box
                key={searchItem.convId}
                onClick={() => setSelectedCustomerID(searchItem.convId)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "16px",
                  borderRadius: "8px",
                  border: `1px solid ${
                    searchItem.convId === selectedCustomerID ? "#297EFF" : "#E2E8F0"
                  }`,
                  cursor: "pointer",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "16px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "16px",
                    }}
                  >
                    <Box
                      sx={{
                        borderRadius: "50%",
                        backgroundColor: "rgba(41, 126, 255, 0.10)",
                        display: "flex",
                        width: "40px",
                        height: "40px",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <DealerAvatarIcon sx={{ fill: "#297EFF" }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: "16px", fontWeight: "600" }}>
                        {searchItem?.firstName} {searchItem?.lastName}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
                        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#64748B" }}>
                          {searchItem?.email}
                        </Typography>
                        <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#64748B" }}>
                          {searchItem?.phone}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Radio
                      checked={searchItem.convId === selectedCustomerID}
                      sx={{ padding: "0", "& .MuiSvgIcon-root": { fill: "#297EFF" } }}
                      onChange={() => setSelectedCustomerID(searchItem.convId)}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCustomerID(searchItem.convId);
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {searchResults.length > 0 && (
          <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              onClick={goToDeal}
              variant="contained"
              sx={{ width: "120px", backgroundColor: "#297EFF", height: "36px" }}
              // disabled={!selectedCustomerID}
            >
              Continue
            </Button>
          </Box>
        )}
      </Box>
    );
  }, [goToDeal, isPage, isSmall, searchLoading, searchResults, searchTerm, selectedCustomerID]);

  const renderSelectCar = useMemo(() => {
    return (
      <Box>
        {carsListMode === "lead-car" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              maxHeight: "360px",
              overflowY: "auto",
            }}
          >
            {carsAlldata.map((car) => (
              <CarItem
                key={car.vin}
                car={car}
                selectedVin={selectedVin || ""}
                setSelectedVin={setSelectedVin}
              />
            ))}
          </Box>
        )}

        {carsListMode === "search" && (
          <Box>
            <Box>
              <ControlledInput
                value={carSearchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  // setSearchLoading(true);
                  setCarSearchTerm(e.target.value);
                }}
                placeholder="Search by VIN, make, model"
                // sx={{ height: "40px" }}
                startAdornment={
                  <InputSearchIcon sx={{ fill: "#C6C6C6", width: "24px", marginRight: "12px" }} />
                }
                sx={{ paddingX: "10px" }}
              />
            </Box>
            <Box
              ref={scrollContainerRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                maxHeight: "360px",
                overflowY: "auto",
                marginTop: "16px",
              }}
            >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "48px",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                carsSearchResults.map((carItem: CarType) => (
                  <CarItem
                    key={carItem.vin}
                    car={carItem}
                    selectedVin={selectedVin || ""}
                    setSelectedVin={setSelectedVin}
                  />
                ))
              )}
              {isFetchingNextPage && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "16px",
                  }}
                >
                  <CircularProgress size={24} />
                </Box>
              )}
            </Box>
          </Box>
        )}

        {carsListMode === "lead-car" && (
          <Box
            sx={{
              marginTop: "16px",
              padding: "16px",
              borderRadius: "8px",
              backgroundColor: "#F9FAFB",
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography sx={{ fontSize: "12px", fontWeight: "400", color: "#64748B" }}>
              If the desired vehicle is not in this list, you can select it from this section.
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddCarIcon sx={{ fill: "#cecece", width: "14px" }} />}
              sx={{ fontSize: "12px" }}
              onClick={handleChangeCarsListMode}
            >
              Select Car
            </Button>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: "1px solid #E2E8F0",
          }}
        >
          <Button
            onClick={goToDeal}
            variant="contained"
            disabled={!selectedVin}
            sx={{ width: "120px", backgroundColor: "#297EFF", height: "36px" }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    );
  }, [
    carSearchTerm,
    carsAlldata,
    carsListMode,
    carsSearchResults,
    goToDeal,
    isFetchingNextPage,
    isLoading,
    selectedVin,
  ]);

  const renderDeal = useMemo(
    () => (
      <DealForm
        values={dealValues}
        onChange={handleChangeDeal}
        users={users ?? []}
        summary={{
          vehicle:
            [selectedCar?.year, selectedCar?.make, selectedCar?.model].filter(Boolean).join(" ") ||
            "Vehicle",
          vin: selectedVin,
          price: selectedCar?.priceOnWebsite ?? selectedCar?.price ?? null,
          photo: selectedCar?.photoURL || selectedCar?.photo || null,
          customer: dealContact().name,
        }}
        reviewMaxEnabled={reviewMaxEnabled}
        isSendReview={isSendReview}
        setIsSendReview={setIsSendReview}
        loading={soldLoading}
        onBack={() => setState(previousState)}
        onSubmit={sendSoldReview}
      />
    ),
    [
      dealContact,
      dealValues,
      handleChangeDeal,
      isSendReview,
      previousState,
      reviewMaxEnabled,
      selectedCar,
      selectedVin,
      sendSoldReview,
      soldLoading,
      users,
    ]
  );

  const renderAddCustomer = useMemo(() => {
    return (
      <Box>
        <Grid
          container
          spacing={2}
          marginTop={"20px"}
          display={"flex"}
          width={"100%"}
          sx={{ flexWrap: "wrap", marginLeft: "0" }}
        >
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="firstName"
              render={({ field }) => (
                <ControlledInput
                  label={"First Name"}
                  error={errors.firstName?.message}
                  id="lead first Name"
                  placeholder="Enter lead first name"
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="lastName"
              render={({ field }) => (
                <ControlledInput
                  label={"Last Name"}
                  error={errors.lastName?.message}
                  id="lastName"
                  placeholder="Enter lead last name"
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="email"
              render={({ field }) => (
                <ControlledInput
                  label={"Email"}
                  error={errors.email?.message}
                  id="email"
                  placeholder="Enter email"
                  {...field}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field }) => (
                <MuiPhone
                  label={"Phone Number"}
                  id="phoneNumber"
                  placeholder="Enter phone number"
                  error={field.value ? errors?.phoneNumber?.message : null}
                  {...field}
                />
              )}
            />
          </Grid>
        </Grid>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            justifyContent: "flex-end",
            marginTop: "16px",
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setState("select-customer")}
            btnColor="#E2E8F0"
            sx={{
              width: "fit-content",
              color: "#020817",
              borderColor: "#E2E8F0",
              height: "40px",
              // "&:hover": { backgroundColor: "#E2E8F0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(goToDeal)}
            variant="contained"
            sx={{ width: "fit-content", backgroundColor: "#297EFF", minWidth: "fit-content" }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    );
  }, [
    control,
    errors.email?.message,
    errors.firstName?.message,
    errors.lastName?.message,
    errors?.phoneNumber?.message,
    goToDeal,
    handleSubmit,
  ]);

  const renderContent = (
    <Box sx={{ width: "100%" }}>
      {state === "review" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {cars.map((car) => (
            <Box
              key={car.vin}
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: "16px",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px",
                borderRadius: "8px",
                border: "1px solid #E2E8F0",
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                <Box>
                  {car.photoURL ? (
                    <img
                      src={car.photoURL}
                      alt={car.model}
                      width={64}
                      height={64}
                      style={{
                        maxWidth: "64px",
                        height: "64px",
                        minWidth: "64px",
                        minHeight: "64px",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: "rgba(41, 126, 255, 0.10)",
                        borderRadius: "8px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        maxWidth: "64px",
                        height: "64px",
                        minWidth: "64px",
                        minHeight: "64px",
                      }}
                    >
                      <CarPlaceholderIcon />
                    </Box>
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: "16px", fontWeight: "600" }}>
                    {car?.year} {car?.make} {car?.model}
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "row", gap: "16px", flexWrap: "wrap" }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "#64748B",
                        minWidth: "160px",
                      }}
                    >
                      stock: {car?.stockNumber}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "400",
                        color: "#64748B",
                        minWidth: "180px",
                      }}
                    >
                      vin: {car?.vin}
                    </Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: "400", color: "#64748B" }}>
                      $ {car?.priceOnWebsite}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                <Button
                  onClick={() => sendAvailable(car.vin)}
                  variant="outlined"
                  btnColor="#E2E8F0"
                  sx={{
                    width: "fit-content",
                    color: "#020817",
                    borderColor: "#E2E8F0",
                    // "&:hover": { backgroundColor: "#E2E8F0" },
                  }}
                >
                  Available
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    setState("select-customer");
                    setSelectedVin(car.vin);
                  }}
                  sx={{ width: "fit-content", backgroundColor: "#297EFF", minWidth: "fit-content" }}
                >
                  Sold
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      ) : state === "select-customer" ? (
        renderSelectCustomer
      ) : state === "select-car" ? (
        renderSelectCar
      ) : state === "deal" ? (
        renderDeal
      ) : (
        renderAddCustomer
      )}
    </Box>
  );

  useEffect(() => {
    searchLeads();
  }, [debouncedSearchTerm, searchLeads]);

  if (isPage) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography sx={{ fontSize: "18px", fontWeight: "600" }}>
          {state === "deal"
            ? "Deal Details"
            : state === "select-customer"
            ? "Select Customer"
            : "Add Customer"}
        </Typography>
        <Box>
          {state === "deal"
            ? renderDeal
            : state === "select-customer"
            ? renderSelectCustomer
            : renderAddCustomer}
        </Box>
        <Snackbar
          handleClose={() => setNotify({ message: "", open: false, type: "" })}
          message={notify.message}
          open={notify.open}
          type={notify.type}
        />
      </Box>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        handleClose={onClose}
        maxWidth="md"
        customWidth={state === "select-car" ? "600px" : "896px"}
        renderTitle={
          <Box sx={{ display: "flex", flexDirection: "row", gap: "8px", alignItems: "center" }}>
            {((state !== "review" && !defaultState) || state === "deal") && (
              <IconButton
                onClick={() => {
                  if (state === "deal") {
                    setState(previousState);
                  } else if (state === "select-customer") {
                    setState("review");
                  } else {
                    setState("select-customer");
                  }
                }}
              >
                <KeyboardBackspace />
              </IconButton>
            )}
            <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
              {state === "review"
                ? "Review Inventory"
                : state === "select-customer"
                ? "Select Customer"
                : state === "select-car"
                ? "Sold Car"
                : state === "deal"
                ? "Deal Details"
                : "Add Customer"}
            </Typography>
          </Box>
        }
        subTitle={
          state === "review"
            ? "Review your inventory and update vehicle status. Mark vehicles as sold or keep them available."
            : state === "select-customer"
            ? "Select the customer who purchased this vehicle"
            : state === "select-car"
            ? "Select the car to be sold"
            : state === "deal"
            ? "Set the sale price, salesperson and the rest of the deal before marking it sold"
            : "Add a new customer to the system"
        }
        renderContent={renderContent}
      />
      <Snackbar
        handleClose={() => setNotify({ message: "", open: false, type: "" })}
        message={notify.message}
        open={notify.open}
        type={notify.type}
      />
    </>
  );
};

export default SoldReview;
